import React from 'react';
import PropTypes from 'prop-types';
import { colors } from '../../theme/colors';
import Typography from '../Typography';
import Button from '../Button';
import {
  FileInputContainer,
  UploadArea,
  DragContent,
  FileList,
  FileItem,
  FileInfo,
  ProgressBarContainer,
  ProgressBarFill,
  DeleteButton,
  HiddenInput,
  ErrorText,
  HelperText,
} from './components/FileInputStyles';
import { useFileInput } from './hooks/useFileInput';
import { getFileInputState } from './utils/fileInputHelpers';

const FileInput = ({
  label = 'Envie um arquivo',
  helperText,
  files = [],
  onDrop,
  onRemove,
  onSend,
  loading = false,
  error,
  disabled = false,
  accept,
  multiple = false,
  ...props
}) => {
  const {
    inputRef,
    isDragActive,
    handleDrag,
    handleDrop,
    handleChange,
    onButtonClick,
  } = useFileInput({ disabled, loading, onDrop, multiple });

  const {
    isRepouso,
    isDropping,
    isCarregamento,
    isPreenchido,
    isErro,
    isDesabilitado,
  } = getFileInputState({ files, isDragActive, error, disabled, loading });

  return (
    <FileInputContainer {...props}>
      {label && (
        <Typography
          variant="small"
          style={{
            fontWeight: 'bold',
            color: isDesabilitado ? '#999' : colors.brandDarkBlue,
          }}
        >
          {label}
        </Typography>
      )}

      {(isRepouso || isDropping || isDesabilitado || (isErro && files.length === 0)) && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <UploadArea
            isDragActive={isDragActive}
            error={error}
            disabled={disabled}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={onButtonClick}
            style={{ flex: 1 }}
          >
            <DragContent>
              <Typography
                size="sm"
                style={{
                  color: error ? colors.danger : disabled ? '#999' : colors.brandMediumGray,
                }}
              >
                {isDropping ? 'Solte o arquivo!' : 'Arraste e solte seu arquivo aqui'}
              </Typography>
            </DragContent>

            <Button
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onButtonClick();
              }}
              style={{
                borderColor: disabled ? '#ccc' : colors.brandDarkBlue,
                color: disabled ? '#ccc' : colors.brandDarkBlue,
                backgroundColor: 'transparent',
              }}
            >
              ESCOLHER ARQUIVO
            </Button>
          </UploadArea>

          <Button
            variant="primary"
            size="md"
            disabled={disabled || files.length === 0}
            onClick={onSend}
          >
            ENVIAR
          </Button>
        </div>
      )}

      {(isCarregamento || isPreenchido || (isErro && files.length > 0)) && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <FileList style={{ flex: 1 }}>
            {files.map((file, index) => (
              <FileItem key={index} error={error}>
                <FileInfo>
                  <i
                    className="far fa-file-alt"
                    style={{
                      color: error ? colors.danger : colors.brandMediumGray,
                      fontSize: '20px',
                    }}
                  ></i>
                  <div style={{ flex: 1 }}>
                    <Typography
                      size="sm"
                      style={{ color: error ? colors.danger : '#333', fontWeight: 'bold' }}
                    >
                      {file.name || 'FileName.pdf'}
                    </Typography>
                    {error && <ErrorText>{error}</ErrorText>}
                    {isCarregamento && (
                      <ProgressBarContainer>
                        <ProgressBarFill
                          progress={typeof loading === 'number' ? loading : 50}
                        />
                      </ProgressBarContainer>
                    )}
                  </div>
                </FileInfo>
                <DeleteButton
                  onClick={() => onRemove && onRemove(index)}
                  disabled={disabled || (loading !== false && typeof loading !== 'number')}
                  error={error}
                >
                  <i className="fas fa-trash-alt"></i>
                </DeleteButton>
              </FileItem>
            ))}
          </FileList>

          <Button
            variant="primary"
            size="md"
            disabled={disabled || isCarregamento || isErro}
            onClick={onSend}
            style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center' }}
          >
            ENVIAR
          </Button>
        </div>
      )}

      {isErro && files.length === 0 && (
        <ErrorText style={{ marginTop: '-8px' }}>{error}</ErrorText>
      )}

      {helperText && !isErro && <HelperText>{helperText}</HelperText>}

      <HiddenInput
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled || loading}
      />
    </FileInputContainer>
  );
};

FileInput.propTypes = {
  label: PropTypes.string,
  helperText: PropTypes.string,
  files: PropTypes.array,
  onDrop: PropTypes.func,
  onRemove: PropTypes.func,
  onSend: PropTypes.func,
  loading: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  error: PropTypes.string,
  disabled: PropTypes.bool,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
};

export default FileInput;



// WEBPACK FOOTER //
// ./src/design-system/atoms/FileInput/FileInput.jsx